import PropTypes from 'prop-types';
import {
  alpha,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  Skeleton,
  Tooltip,
  Typography,
} from '@mui/material';
import { Close, DownloadRounded } from '@mui/icons-material';
import {
  Bar,
  Cell,
  ComposedChart,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip as RTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  ACTION_META,
  LABEL_META,
  convStrength,
  fmtDec,
  formatAvgVolumeTitle,
  formatCompactVolume,
  formatDrawerMoneyFlowLabel,
  formatDrawerScoreChipLabel,
  formatLabelMixTooltip,
  getDrawerScoreLevel,
  getHistoryChartLegendLabels,
  getLevel,
  getLiquidityColor,
  getLiquidityKey,
  getMoneyFlowMeta,
  getPriceChangeColor,
  getRiskChipColors,
} from './shared';

const historyEntryShape = PropTypes.shape({
  behavior_label: PropTypes.string,
  close_price: PropTypes.number,
  conviction: PropTypes.number,
  conviction_delta: PropTypes.number,
  days_in_label: PropTypes.number,
  phase: PropTypes.string,
  prev_label: PropTypes.string,
  risk_label: PropTypes.string,
  scores: PropTypes.shape({
    coil: PropTypes.number,
    distribution: PropTypes.number,
    markup: PropTypes.number,
    support: PropTypes.number,
  }),
  strength_tier: PropTypes.string,
  tactical_action: PropTypes.string,
  trade_date: PropTypes.string,
  volume: PropTypes.number,
});

const snapshotShape = PropTypes.shape({
  avg_value_20d: PropTypes.number,
  avg_volume_20d: PropTypes.number,
  behavior_label: PropTypes.string,
  close_price: PropTypes.number,
  dist_to_high_20d_pct: PropTypes.number,
  dist_to_low_20d_pct: PropTypes.number,
  high_20d: PropTypes.number,
  is_defensive_strength: PropTypes.bool,
  is_market_red_stock_green: PropTypes.bool,
  low_20d: PropTypes.number,
  market_regime: PropTypes.string,
  money_flow_label: PropTypes.string,
  phase: PropTypes.string,
  price_change_pct: PropTypes.number,
  resilience_score: PropTypes.number,
  risk_label: PropTypes.string,
  sector_code: PropTypes.string,
  sector_en: PropTypes.string,
  sector_vi: PropTypes.string,
  smart_money_label: PropTypes.string,
  smart_money_label_display: PropTypes.string,
  smart_money_label_key: PropTypes.string,
  stealth_score: PropTypes.number,
  strength_score: PropTypes.number,
  strength_tier: PropTypes.string,
  value_ratio: PropTypes.number,
  volume: PropTypes.number,
  vpa_setup: PropTypes.string,
  vpa_setup_display: PropTypes.string,
  vpa_setup_key: PropTypes.string,
});

const statsShape = PropTypes.shape({
  avg_conviction: PropTypes.number,
  current_streak_days: PropTypes.number,
  label_changes: PropTypes.number,
  label_distribution: PropTypes.objectOf(PropTypes.number),
  window_days: PropTypes.number,
});

const tickerHistoryShape = PropTypes.shape({
  company_name: PropTypes.string,
  error: PropTypes.string,
  history: PropTypes.arrayOf(historyEntryShape),
  snapshot: snapshotShape,
  stats: statsShape,
});

const refShape = PropTypes.oneOfType([
  PropTypes.func,
  PropTypes.shape({ current: PropTypes.any }),
]);

const HISTORY_TOOLTIP_PAYLOAD_SHAPE = PropTypes.shape({
  payload: PropTypes.shape({
    behaviorLabel: PropTypes.string,
    color: PropTypes.string,
    conviction: PropTypes.number,
    labelChanged: PropTypes.bool,
    phase: PropTypes.string,
    prevLabel: PropTypes.string,
    tradeDate: PropTypes.string,
  }),
});

const DrawerScoreLevelChip = ({ scoreValue, titleKey, t }) => {
  if (scoreValue == null) return null;

  const level = getDrawerScoreLevel(scoreValue);

  return (
    <Tooltip arrow title={`${t(titleKey)}: ${fmtDec(scoreValue, 0)}/100`}>
      <Chip
        size="small"
        label={formatDrawerScoreChipLabel(t, titleKey, level.key)}
        sx={{ height: 20, fontSize: '0.62rem', fontWeight: 700, bgcolor: alpha(level.color, 0.1), color: level.color }}
      />
    </Tooltip>
  );
};

DrawerScoreLevelChip.propTypes = {
  scoreValue: PropTypes.number,
  t: PropTypes.func.isRequired,
  titleKey: PropTypes.string.isRequired,
};

const HistoryChartTooltip = ({ active, payload, t }) => {
  if (!active || !payload?.length) return null;

  const point = payload[0].payload;
  const meta = LABEL_META[point.behaviorLabel] || LABEL_META.NEUTRAL;

  return (
    <Box sx={{ bgcolor: '#fff', border: '1px solid #e2e8f0', borderRadius: 1.5, p: 1.1, boxShadow: '0 6px 18px rgba(15, 23, 42, 0.08)' }}>
      <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#0f172a', mb: 0.25 }}>
        {point.tradeDate}
      </Typography>
      <Typography sx={{ fontSize: '0.7rem', color: meta.color, fontWeight: 700, mb: 0.35 }}>
        {t(`behavior.labels.${point.behaviorLabel}.short`)}
      </Typography>
      <Typography sx={{ fontSize: '0.66rem', color: '#475569' }}>
        {t('behavior.table.conviction')}: {fmtDec(point.conviction, 1)}
      </Typography>
      {point.labelChanged && point.prevLabel && (
        <Typography sx={{ fontSize: '0.66rem', color: '#475569' }}>
          {t(`behavior.labels.${point.prevLabel}.short`)} {' -> '} {t(`behavior.labels.${point.behaviorLabel}.short`)}
        </Typography>
      )}
      {point.phase && (
        <Typography sx={{ fontSize: '0.66rem', color: '#64748b' }}>
          {t(`behavior.phases.${point.phase}`, { defaultValue: point.phase })}
        </Typography>
      )}
    </Box>
  );
};

HistoryChartTooltip.propTypes = {
  active: PropTypes.bool,
  payload: PropTypes.arrayOf(HISTORY_TOOLTIP_PAYLOAD_SHAPE),
  t: PropTypes.func.isRequired,
};

const HistoryChartDot = ({ cx, cy, payload }) => {
  if (cx == null || cy == null) return null;

  return (
    <circle
      cx={cx}
      cy={cy}
      r={payload.labelChanged ? 3.4 : 2.2}
      fill={payload.color}
      stroke="#fff"
      strokeWidth={payload.labelChanged ? 1.6 : 1}
    />
  );
};

HistoryChartDot.propTypes = {
  cx: PropTypes.number,
  cy: PropTypes.number,
  payload: PropTypes.shape({
    color: PropTypes.string,
    labelChanged: PropTypes.bool,
  }).isRequired,
};

const TickerHistoryLoadingState = () => (
  <>
    <Card sx={{ mb: 2, border: '1px solid #e2e8f0', boxShadow: 'none' }}>
      <CardContent sx={{ py: 1.75, px: 2, '&:last-child': { pb: 1.75 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
          <Skeleton variant="rounded" width={120} height={24} />
          <Skeleton variant="text" width={70} height={16} />
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 1.5 }}>
          {[0, 1, 2].map((item) => (
            <Box key={item}>
              <Skeleton variant="text" width="60%" height={10} />
              <Skeleton variant="text" width="80%" height={20} />
              <Skeleton variant="rounded" width="100%" height={4} sx={{ mt: 0.5 }} />
            </Box>
          ))}
        </Box>
        <Divider sx={{ my: 1.25 }} />
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 1 }}>
          {[0, 1, 2, 3].map((item) => (
            <Box key={item} sx={{ textAlign: 'center' }}>
              <Skeleton variant="text" width="60%" height={10} sx={{ mx: 'auto' }} />
              <Skeleton variant="text" width="40%" height={18} sx={{ mx: 'auto' }} />
              <Skeleton variant="rounded" width="70%" height={3} sx={{ mx: 'auto', mt: 0.5 }} />
            </Box>
          ))}
        </Box>
      </CardContent>
    </Card>

    <Card sx={{ mb: 2, border: '1px solid #e2e8f0', boxShadow: 'none' }}>
      <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
        <Skeleton variant="text" width="40%" height={12} sx={{ mb: 1 }} />
        <Skeleton variant="text" width="50%" height={28} />
        <Skeleton variant="rounded" width="100%" height={6} sx={{ mt: 1, mb: 1.5 }} />
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
          {[60, 80, 70, 90].map((width) => (
            <Skeleton key={`snapshot-skeleton-${width}`} variant="rounded" width={width} height={20} />
          ))}
        </Box>
      </CardContent>
    </Card>

    <Card sx={{ mb: 2, border: '1px solid #e2e8f0', boxShadow: 'none' }}>
      <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
        <Skeleton variant="text" width="40%" height={12} sx={{ mb: 1 }} />
        <Box sx={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr 1fr', gap: 1.5, mb: 1.5 }}>
          {[0, 1, 2].map((item) => (
            <Box key={item}>
              <Skeleton variant="text" width="70%" height={10} />
              <Skeleton variant="text" width="60%" height={20} />
            </Box>
          ))}
        </Box>
        <Skeleton variant="rounded" width="100%" height={8} sx={{ mb: 1 }} />
        <Skeleton variant="text" width="100%" height={14} />
        <Skeleton variant="text" width="90%" height={14} />
      </CardContent>
    </Card>

    <Card sx={{ mb: 2, border: '1px solid #e2e8f0', boxShadow: 'none' }}>
      <CardContent sx={{ py: 1.5, px: 1.5, '&:last-child': { pb: 1 } }}>
        <Skeleton variant="text" width="40%" height={12} sx={{ mb: 1 }} />
        <Skeleton variant="rounded" width="100%" height={140} />
        <Skeleton variant="rounded" width="100%" height={70} sx={{ mt: 0.5 }} />
      </CardContent>
    </Card>

    <Skeleton variant="text" width="50%" height={14} sx={{ mb: 1 }} />
    {[0, 1, 2, 3, 4].map((item) => (
      <Box key={item} sx={{ display: 'flex', gap: 1.5, mb: 1.5, alignItems: 'flex-start' }}>
        <Skeleton variant="circular" width={32} height={32} sx={{ flexShrink: 0 }} />
        <Box sx={{ flex: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Skeleton variant="text" width="40%" height={14} />
            <Skeleton variant="text" width="20%" height={12} />
          </Box>
          <Skeleton variant="text" width="80%" height={12} />
        </Box>
      </Box>
    ))}
  </>
);

const TickerHistoryCurrentStatusCard = ({ current, t }) => {
  const meta = LABEL_META[current.behavior_label] || LABEL_META.NEUTRAL;
  const convictionMeta = convStrength(current.conviction);
  const subscores = [
    { key: 'support', label: t('behavior.table.support'), color: '#f59e0b', val: current.scores?.support },
    { key: 'coil', label: t('behavior.table.coil'), color: '#3b82f6', val: current.scores?.coil },
    { key: 'markup', label: t('behavior.table.markup'), color: '#10b981', val: current.scores?.markup },
    { key: 'dist', label: t('behavior.table.distribution'), color: '#ef4444', val: current.scores?.distribution },
  ];

  return (
    <Card sx={{ mb: 2, border: `1.5px solid ${meta.color}`, bgcolor: alpha(meta.color, 0.03), boxShadow: 'none' }}>
      <CardContent sx={{ py: 1.75, px: 2, '&:last-child': { pb: 1.75 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
          <Chip
            label={t(`behavior.labels.${current.behavior_label}.title`)}
            size="small"
            sx={{ height: 24, fontSize: '0.72rem', fontWeight: 700, bgcolor: meta.color, color: 'white', px: 0.5 }}
          />
          <Typography sx={{ fontSize: '0.68rem', color: '#94a3b8', fontFamily: 'monospace' }}>{current.trade_date}</Typography>
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 1.5, alignItems: 'start' }}>
          <Box>
            <Typography sx={{ fontSize: '0.58rem', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.04em', mb: 0.5 }}>
              {t('behavior.table.conviction')}
            </Typography>
            <Typography sx={{ fontSize: '0.9rem', fontWeight: 800, color: convictionMeta.color, lineHeight: 1.2 }}>
              {convictionMeta.label}
            </Typography>
            <Box sx={{ mt: 0.5, width: '100%', height: 4, bgcolor: '#f1f5f9', borderRadius: 2, overflow: 'hidden' }}>
              <Box sx={{ width: `${convictionMeta.pct * 100}%`, height: '100%', bgcolor: convictionMeta.color, borderRadius: 2 }} />
            </Box>
          </Box>

          <Box>
            <Typography sx={{ fontSize: '0.58rem', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.04em', mb: 0.5 }}>
              {t('behavior.table.action')}
            </Typography>
            <Chip
              label={t(`behavior.actions.${current.tactical_action || 'IGNORE'}`)}
              size="small"
              sx={{
                height: 22,
                fontSize: '0.68rem',
                fontWeight: 700,
                bgcolor: alpha(ACTION_META[current.tactical_action]?.color || '#94a3b8', 0.12),
                color: ACTION_META[current.tactical_action]?.color || '#94a3b8',
              }}
            />
          </Box>

          <Box>
            <Typography sx={{ fontSize: '0.58rem', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.04em', mb: 0.5 }}>
              {t('behavior.history.daysInLabel')}
            </Typography>
            <Typography sx={{ fontSize: '1.1rem', fontWeight: 800, fontFamily: 'monospace', color: '#0f172a', lineHeight: 1.2 }}>
              {current.days_in_label || 1}
              <Typography component="span" sx={{ fontSize: '0.6rem', color: '#94a3b8', fontWeight: 500, ml: 0.3 }}>
                {t('behavior.history.days')}
              </Typography>
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ my: 1.25 }} />
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 1 }}>
          {subscores.map((subscore) => {
            const level = getLevel(subscore.val || 0);
            const isActive = (subscore.val || 0) >= 15;

            return (
              <Tooltip key={subscore.key} arrow placement="top" title={`${subscore.label}: ${fmtDec(subscore.val, 0)}/100`}>
                <Box sx={{ textAlign: 'center', cursor: 'default' }}>
                  <Typography sx={{ fontSize: '0.55rem', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.03em', mb: 0.3 }}>
                    {subscore.label}
                  </Typography>
                  <Typography sx={{ fontSize: '0.78rem', fontWeight: 800, color: isActive ? subscore.color : '#cbd5e1', lineHeight: 1.2 }}>
                    {t(`behavior.levels.${level.key}`)}
                  </Typography>
                  <Box sx={{ mt: 0.4, mx: 'auto', width: '70%', height: 3, bgcolor: '#f1f5f9', borderRadius: 2, overflow: 'hidden' }}>
                    <Box sx={{ width: `${Math.min(100, Math.max(0, subscore.val || 0))}%`, height: '100%', bgcolor: isActive ? subscore.color : '#e2e8f0', borderRadius: 2 }} />
                  </Box>
                </Box>
              </Tooltip>
            );
          })}
        </Box>
      </CardContent>
    </Card>
  );
};

TickerHistoryCurrentStatusCard.propTypes = {
  current: historyEntryShape.isRequired,
  t: PropTypes.func.isRequired,
};

const TickerHistorySnapshotCard = ({ language, snapshot, t }) => {
  const sectorLabel = language === 'en' ? (snapshot.sector_en || snapshot.sector_code) : (snapshot.sector_vi || snapshot.sector_code);
  const priceChangeColor = getPriceChangeColor(snapshot.price_change_pct);
  const valueRatio = snapshot.value_ratio;
  const liquidityColor = getLiquidityColor(getLiquidityKey(valueRatio));
  const riskChipColors = getRiskChipColors(snapshot.risk_label);
  const low = snapshot.low_20d;
  const high = snapshot.high_20d;
  const close = snapshot.close_price;
  const position = low != null && high != null && high > low && close != null ? ((close - low) / (high - low)) * 100 : null;

  return (
    <Card sx={{ mb: 2, border: '1px solid #e2e8f0', boxShadow: 'none' }}>
      <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            {t('behavior.drawer.priceSnapshot')}
          </Typography>
          {sectorLabel && (
            <Chip label={sectorLabel} size="small" sx={{ height: 18, fontSize: '0.6rem', bgcolor: '#f1f5f9', color: '#475569' }} />
          )}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 1 }}>
          <Typography sx={{ fontSize: '1.35rem', fontWeight: 800, fontFamily: 'monospace', color: '#0f172a' }}>
            {close == null ? '—' : close.toLocaleString('vi-VN')}
          </Typography>
          {snapshot.price_change_pct != null && (
            <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: priceChangeColor }}>
              {snapshot.price_change_pct > 0 ? '+' : ''}{fmtDec(snapshot.price_change_pct, 2)}%
            </Typography>
          )}
          <Box sx={{ flex: 1 }} />
          <Tooltip arrow placement="top" title={formatAvgVolumeTitle(t, snapshot.avg_volume_20d, snapshot.avg_value_20d)}>
            <Typography sx={{ fontSize: '0.68rem', color: '#94a3b8', cursor: 'default' }}>
              {t('behavior.drawer.volume')}: <b style={{ color: '#475569' }}>{formatCompactVolume(snapshot.volume)}</b>
              {valueRatio != null && (
                <>
                  {' · '}
                  <span style={{ color: liquidityColor, fontWeight: 700 }}>×{fmtDec(valueRatio, 2)}</span>
                </>
              )}
            </Typography>
          </Tooltip>
        </Box>

        {position != null && (
          <Box sx={{ mb: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.3 }}>
              <Typography sx={{ fontSize: '0.6rem', color: '#94a3b8' }}>
                {t('behavior.drawer.distLow20d')}: <b style={{ color: '#475569' }}>+{fmtDec(snapshot.dist_to_low_20d_pct, 1)}%</b>
              </Typography>
              <Typography sx={{ fontSize: '0.6rem', color: '#94a3b8' }}>
                {t('behavior.drawer.distHigh20d')}: <b style={{ color: '#475569' }}>−{fmtDec(snapshot.dist_to_high_20d_pct, 1)}%</b>
              </Typography>
            </Box>
            <Box sx={{ position: 'relative', height: 6, bgcolor: '#f1f5f9', borderRadius: 3 }}>
              <Box
                sx={{
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  height: '100%',
                  width: `${Math.max(0, Math.min(100, position))}%`,
                  background: 'linear-gradient(90deg,#f59e0b 0%,#3b82f6 50%,#10b981 100%)',
                  borderRadius: 3,
                }}
              />
              <Box sx={{ position: 'absolute', left: `${Math.max(0, Math.min(100, position))}%`, top: -2, width: 2, height: 10, bgcolor: '#0f172a', transform: 'translateX(-1px)' }} />
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.3 }}>
              <Typography sx={{ fontSize: '0.55rem', color: '#cbd5e1', fontFamily: 'monospace' }}>{low?.toLocaleString('vi-VN')}</Typography>
              <Typography sx={{ fontSize: '0.55rem', color: '#cbd5e1', fontFamily: 'monospace' }}>{high?.toLocaleString('vi-VN')}</Typography>
            </Box>
          </Box>
        )}

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {snapshot.phase && (
            <Chip
              size="small"
              label={t(`behavior.drawer.phases.${snapshot.phase}`, { defaultValue: snapshot.phase })}
              sx={{ height: 20, fontSize: '0.62rem', bgcolor: alpha('#0ea5e9', 0.1), color: '#0369a1', fontWeight: 700 }}
            />
          )}
          {snapshot.risk_label && (
            <Chip
              size="small"
              label={t(`behavior.drawer.risks.${snapshot.risk_label}`, { defaultValue: snapshot.risk_label })}
              sx={{ height: 20, fontSize: '0.62rem', fontWeight: 700, bgcolor: alpha(riskChipColors.bg, 0.12), color: riskChipColors.text }}
            />
          )}
          <DrawerScoreLevelChip scoreValue={snapshot.strength_score} titleKey="behavior.drawer.strengthScore" t={t} />
          <DrawerScoreLevelChip scoreValue={snapshot.resilience_score} titleKey="behavior.drawer.resilience" t={t} />
          {snapshot.stealth_score != null && snapshot.stealth_score >= 40 && (
            <Tooltip arrow title={`${t('behavior.drawer.stealth')}: ${fmtDec(snapshot.stealth_score, 0)}/100`}>
              <Chip size="small" label={t('behavior.drawer.stealth')} sx={{ height: 20, fontSize: '0.62rem', bgcolor: alpha('#14b8a6', 0.12), color: '#0f766e', fontWeight: 700 }} />
            </Tooltip>
          )}
          {snapshot.strength_tier && (
            <Chip size="small" label={t(`behavior.drawer.tiers.${snapshot.strength_tier}`, { defaultValue: snapshot.strength_tier })} sx={{ height: 20, fontSize: '0.62rem', bgcolor: alpha('#10b981', 0.12), color: '#047857', fontWeight: 700 }} />
          )}
          {snapshot.smart_money_label && (
            <Chip
              size="small"
              label={`${t('behavior.drawer.smartMoney')}: ${t(snapshot.smart_money_label_key || '', { defaultValue: snapshot.smart_money_label_display || snapshot.smart_money_label })}`}
              sx={{ height: 20, fontSize: '0.62rem', bgcolor: alpha('#7c3aed', 0.1), color: '#5b21b6', fontWeight: 700 }}
            />
          )}
          {snapshot.market_regime && (
            <Chip size="small" label={snapshot.market_regime} sx={{ height: 20, fontSize: '0.62rem', bgcolor: '#f1f5f9', color: '#475569' }} />
          )}
          {snapshot.vpa_setup && snapshot.vpa_setup !== 'neutral' && (
            <Chip
              size="small"
              label={`${t('behavior.drawer.vpaSetup')}: ${t(snapshot.vpa_setup_key || '', { defaultValue: snapshot.vpa_setup_display || snapshot.vpa_setup })}`}
              sx={{ height: 20, fontSize: '0.62rem', bgcolor: '#f1f5f9', color: '#475569' }}
            />
          )}
          {snapshot.money_flow_label && (
            <Chip
              size="small"
              label={formatDrawerMoneyFlowLabel(t, snapshot.money_flow_label)}
              sx={{ height: 20, fontSize: '0.62rem', bgcolor: getMoneyFlowMeta(snapshot.money_flow_label).bg, color: getMoneyFlowMeta(snapshot.money_flow_label).color }}
            />
          )}
          {snapshot.is_defensive_strength && (
            <Chip size="small" label={t('behavior.drawer.defensiveBadge')} sx={{ height: 20, fontSize: '0.62rem', bgcolor: alpha('#f59e0b', 0.15), color: '#92400e', fontWeight: 700 }} />
          )}
          {snapshot.is_market_red_stock_green && (
            <Chip size="small" label={t('behavior.drawer.redGreenBadge')} sx={{ height: 20, fontSize: '0.62rem', bgcolor: alpha('#10b981', 0.15), color: '#065f46', fontWeight: 700 }} />
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

TickerHistorySnapshotCard.propTypes = {
  language: PropTypes.string.isRequired,
  snapshot: snapshotShape.isRequired,
  t: PropTypes.func.isRequired,
};

const TickerHistoryStatsCard = ({ currentLabel, historyLength, snapshotLabel, stats, t }) => {
  const distribution = stats.label_distribution || {};
  const total = Object.values(distribution).reduce((sum, count) => sum + count, 0) || 1;
  const orderedLabels = ['ACTIVE_MARKUP', 'COILING_FOR_MARKUP', 'SUPPORT_DEFENSE', 'FAKE_SUPPORT_OR_EXIT', 'NEUTRAL']
    .filter((label) => distribution[label]);
  const avgConvictionMeta = convStrength(stats.avg_conviction);
  const effectiveLabel = currentLabel || snapshotLabel || 'NEUTRAL';

  return (
    <Card sx={{ mb: 2, border: '1px solid #e2e8f0', boxShadow: 'none' }}>
      <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
        <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 1 }}>
          {t('behavior.drawer.statsTitle', { days: stats.window_days || historyLength })}
        </Typography>

        <Box sx={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr 1fr', gap: 1.5, mb: 1.5, alignItems: 'start' }}>
          <Box>
            <Typography sx={{ fontSize: '0.55rem', color: '#94a3b8', textTransform: 'uppercase' }}>{t('behavior.drawer.avgConviction')}</Typography>
            <Tooltip arrow placement="top" title={`${stats.avg_conviction > 0 ? '+' : ''}${fmtDec(stats.avg_conviction, 1)}`}>
              <Box sx={{ cursor: 'default' }}>
                <Typography sx={{ fontSize: '0.95rem', fontWeight: 800, color: avgConvictionMeta.color, lineHeight: 1.2 }}>
                  {avgConvictionMeta.label}
                </Typography>
                <Box sx={{ mt: 0.4, width: '100%', height: 4, bgcolor: '#f1f5f9', borderRadius: 2, overflow: 'hidden' }}>
                  <Box sx={{ width: `${avgConvictionMeta.pct * 100}%`, height: '100%', bgcolor: avgConvictionMeta.color, borderRadius: 2 }} />
                </Box>
              </Box>
            </Tooltip>
          </Box>
          <Box>
            <Typography sx={{ fontSize: '0.55rem', color: '#94a3b8', textTransform: 'uppercase' }}>{t('behavior.drawer.currentStreak')}</Typography>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, lineHeight: 1.2 }}>
              <Typography sx={{ fontSize: '1rem', fontWeight: 800, fontFamily: 'monospace', color: '#0f172a' }}>
                {stats.current_streak_days}
              </Typography>
              <Typography sx={{ fontSize: '0.58rem', color: '#94a3b8' }}>{t('behavior.drawer.sessionsUnit')}</Typography>
            </Box>
            {snapshotLabel && (
              <Typography sx={{ fontSize: '0.6rem', color: (LABEL_META[snapshotLabel] || LABEL_META.NEUTRAL).color, fontWeight: 700, mt: 0.25, lineHeight: 1.2 }}>
                {t(`behavior.labels.${snapshotLabel}.short`)}
              </Typography>
            )}
          </Box>
          <Box>
            <Typography sx={{ fontSize: '0.55rem', color: '#94a3b8', textTransform: 'uppercase' }}>{t('behavior.drawer.labelChanges')}</Typography>
            <Typography sx={{ fontSize: '1rem', fontWeight: 800, fontFamily: 'monospace', color: '#0f172a' }}>
              {stats.label_changes}
            </Typography>
          </Box>
        </Box>

        <Typography sx={{ fontSize: '0.55rem', color: '#94a3b8', textTransform: 'uppercase', mb: 0.5 }}>{t('behavior.drawer.labelMix')}</Typography>
        <Box sx={{ display: 'flex', height: 8, borderRadius: 4, overflow: 'hidden', mb: 0.5, bgcolor: '#f1f5f9' }}>
          {orderedLabels.map((label) => {
            const width = (distribution[label] / total) * 100;

            return (
              <Tooltip key={label} arrow placement="top" title={formatLabelMixTooltip(t, label, distribution[label], width)}>
                <Box sx={{ width: `${width}%`, height: '100%', bgcolor: LABEL_META[label]?.color || '#94a3b8' }} />
              </Tooltip>
            );
          })}
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
          {orderedLabels.map((label) => (
            <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: LABEL_META[label]?.color || '#94a3b8' }} />
              <Typography sx={{ fontSize: '0.6rem', color: '#475569' }}>
                {t(`behavior.labels.${label}.title`)} · <b>{distribution[label]}</b>
              </Typography>
            </Box>
          ))}
        </Box>

        <Divider sx={{ my: 1 }} />
        <Typography sx={{ fontSize: '0.55rem', color: '#94a3b8', textTransform: 'uppercase', mb: 0.3 }}>
          {t('behavior.drawer.interpretationTitle')}
        </Typography>
        <Typography sx={{ fontSize: '0.72rem', color: '#334155', lineHeight: 1.5 }}>
          {t(`behavior.drawer.interpretation.${effectiveLabel}`, { defaultValue: t('behavior.drawer.interpretation.NEUTRAL') })}
        </Typography>
      </CardContent>
    </Card>
  );
};

TickerHistoryStatsCard.propTypes = {
  currentLabel: PropTypes.string,
  historyLength: PropTypes.number.isRequired,
  snapshotLabel: PropTypes.string,
  stats: statsShape.isRequired,
  t: PropTypes.func.isRequired,
};

TickerHistoryStatsCard.defaultProps = {
  currentLabel: null,
  snapshotLabel: null,
};

const TickerHistoryPriceChartCard = ({ history, t }) => {
  if (history.length < 5) return null;

  const chartData = history.slice().reverse().map((entry) => ({
    date: (entry.trade_date || '').slice(5),
    close: entry.close_price,
    conviction: entry.conviction,
    label: entry.behavior_label,
    volume: entry.volume,
  }));
  const hasPrice = chartData.some((point) => point.close != null);
  const maxVolume = Math.max(...chartData.map((point) => point.volume || 0));

  return (
    <Card sx={{ mb: 2, border: '1px solid #e2e8f0', boxShadow: 'none' }}>
      <CardContent sx={{ py: 1.5, px: 1.5, '&:last-child': { pb: 1 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5, px: 0.5 }}>
          <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            {t('behavior.drawer.chartTitle')}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
              <Box sx={{ width: 10, height: 2, bgcolor: '#0f172a' }} />
              <Typography sx={{ fontSize: '0.58rem', color: '#64748b' }}>{t('behavior.drawer.chartPrice')}</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
              <Box sx={{ width: 8, height: 8, bgcolor: alpha('#3b82f6', 0.5), borderRadius: 0.5 }} />
              <Typography sx={{ fontSize: '0.58rem', color: '#64748b' }}>{t('behavior.drawer.chartVolume')}</Typography>
            </Box>
          </Box>
        </Box>

        <ResponsiveContainer width="100%" height={140}>
          <ComposedChart data={chartData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
            <XAxis dataKey="date" tick={{ fontSize: 9, fill: '#94a3b8' }} interval="preserveStartEnd" axisLine={{ stroke: '#e2e8f0' }} tickLine={false} />
            <YAxis yAxisId="price" tick={{ fontSize: 9, fill: '#94a3b8' }} axisLine={false} tickLine={false} width={42} domain={hasPrice ? ['dataMin - 1', 'dataMax + 1'] : [0, 1]} />
            <YAxis yAxisId="vol" orientation="right" hide domain={[0, maxVolume * 2]} />
            <RTooltip
              contentStyle={{ fontSize: 11, borderRadius: 6, border: '1px solid #e2e8f0', padding: '4px 8px' }}
              labelStyle={{ fontWeight: 700, color: '#0f172a' }}
              formatter={(value, name) => {
                if (name === t('behavior.drawer.chartPrice')) return [value?.toLocaleString('vi-VN'), name];
                if (name === t('behavior.drawer.chartVolume')) return [`${(value / 1000).toFixed(0)}K`, name];
                return [value, name];
              }}
            />
            <Bar yAxisId="vol" dataKey="volume" name={t('behavior.drawer.chartVolume')} fill={alpha('#3b82f6', 0.35)} radius={[2, 2, 0, 0]} />
            <Line yAxisId="price" type="monotone" dataKey="close" name={t('behavior.drawer.chartPrice')} stroke="#0f172a" strokeWidth={2} dot={false} activeDot={{ r: 3 }} />
          </ComposedChart>
        </ResponsiveContainer>

        <ResponsiveContainer width="100%" height={70}>
          <ComposedChart data={chartData} margin={{ top: 4, right: 8, left: 0, bottom: 4 }}>
            <XAxis dataKey="date" hide />
            <YAxis tick={{ fontSize: 9, fill: '#94a3b8' }} axisLine={false} tickLine={false} width={42} domain={[-50, 50]} ticks={[-50, 0, 50]} />
            <ReferenceLine y={0} stroke="#cbd5e1" strokeDasharray="2 2" />
            <RTooltip
              contentStyle={{ fontSize: 11, borderRadius: 6, border: '1px solid #e2e8f0', padding: '4px 8px' }}
              formatter={(value) => [fmtDec(value, 1), t('behavior.drawer.chartConviction')]}
            />
            <Bar dataKey="conviction" name={t('behavior.drawer.chartConviction')} radius={[2, 2, 2, 2]}>
              {chartData.map((point) => (
                <Cell key={`${point.date}-${point.label}`} fill={(LABEL_META[point.label] || LABEL_META.NEUTRAL).color} fillOpacity={0.75} />
              ))}
            </Bar>
          </ComposedChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
};

TickerHistoryPriceChartCard.propTypes = {
  history: PropTypes.arrayOf(historyEntryShape).isRequired,
  t: PropTypes.func.isRequired,
};

const TickerHistoryTimelineChart = ({ history, stats, t }) => {
  const chartData = history.slice().reverse().map((entry, index) => {
    const meta = LABEL_META[entry.behavior_label] || LABEL_META.NEUTRAL;
    const labelChanged = entry.prev_label && entry.prev_label !== entry.behavior_label;

    return {
      behaviorLabel: entry.behavior_label,
      color: meta.color,
      conviction: entry.conviction ?? 0,
      convictionDelta: entry.conviction_delta,
      date: (entry.trade_date || '').slice(5),
      index,
      labelChanged,
      phase: entry.phase,
      prevLabel: entry.prev_label,
      riskLabel: entry.risk_label,
      stateBand: 0.7,
      strengthTier: entry.strength_tier,
      tradeDate: entry.trade_date,
    };
  });
  const convictionLimit = Math.max(20, ...chartData.map((point) => Math.abs(point.conviction || 0)));
  const legendLabels = getHistoryChartLegendLabels(chartData);

  return (
    <Card sx={{ mb: 0.5, border: '1px solid #e2e8f0', boxShadow: 'none' }}>
      <CardContent sx={{ py: 1.5, px: 1.5, '&:last-child': { pb: 1.25 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1, mb: 1 }}>
          <Box>
            <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              {t('behavior.history.timeline')} ({history.length} {t('behavior.history.days')})
            </Typography>
            <Typography sx={{ fontSize: '0.62rem', color: '#94a3b8', mt: 0.25 }}>
              {chartData[0]?.date} - {chartData[chartData.length - 1]?.date}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            <Chip size="small" label={`${t('behavior.drawer.labelChanges')}: ${stats?.label_changes ?? 0}`} sx={{ height: 20, fontSize: '0.6rem', fontWeight: 700, bgcolor: '#f8fafc', color: '#475569' }} />
            <Chip size="small" label={`${t('behavior.drawer.currentStreak')}: ${stats?.current_streak_days ?? 0}`} sx={{ height: 20, fontSize: '0.6rem', fontWeight: 700, bgcolor: '#f8fafc', color: '#475569' }} />
          </Box>
        </Box>

        <ResponsiveContainer width="100%" height={156}>
          <ComposedChart data={chartData} margin={{ top: 6, right: 8, left: 0, bottom: 0 }}>
            <XAxis dataKey="date" tick={{ fontSize: 9, fill: '#94a3b8' }} interval="preserveStartEnd" minTickGap={18} axisLine={{ stroke: '#e2e8f0' }} tickLine={false} />
            <YAxis yAxisId="conviction" tick={{ fontSize: 9, fill: '#94a3b8' }} axisLine={false} tickLine={false} width={34} domain={[-convictionLimit, convictionLimit]} ticks={[-convictionLimit, 0, convictionLimit]} />
            <YAxis yAxisId="labelBand" hide domain={[0, 5]} />
            <ReferenceLine yAxisId="conviction" y={0} stroke="#cbd5e1" strokeDasharray="3 3" />
            <RTooltip content={<HistoryChartTooltip t={t} />} />
            <Bar yAxisId="labelBand" dataKey="stateBand" barSize={12} radius={[4, 4, 0, 0]}>
              {chartData.map((point) => (
                <Cell
                  key={`${point.tradeDate}-${point.behaviorLabel}`}
                  fill={alpha(point.color, 0.22)}
                  stroke={point.labelChanged ? point.color : 'none'}
                  strokeWidth={point.labelChanged ? 1.5 : 0}
                />
              ))}
            </Bar>
            <Line yAxisId="conviction" type="monotone" dataKey="conviction" stroke="#0f172a" strokeWidth={1.8} activeDot={{ r: 4, fill: '#0f172a', stroke: '#fff', strokeWidth: 2 }} dot={<HistoryChartDot />} />
          </ComposedChart>
        </ResponsiveContainer>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1 }}>
          {legendLabels.map((label) => {
            const meta = LABEL_META[label] || LABEL_META.NEUTRAL;

            return (
              <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                <Box sx={{ width: 8, height: 8, borderRadius: 0.8, bgcolor: alpha(meta.color, 0.28), border: `1px solid ${alpha(meta.color, 0.45)}` }} />
                <Typography sx={{ fontSize: '0.6rem', color: '#64748b' }}>
                  {t(`behavior.labels.${label}.short`)}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </CardContent>
    </Card>
  );
};

TickerHistoryTimelineChart.propTypes = {
  history: PropTypes.arrayOf(historyEntryShape).isRequired,
  stats: statsShape,
  t: PropTypes.func.isRequired,
};

TickerHistoryTimelineChart.defaultProps = {
  stats: null,
};

const TickerHistoryDrawerBody = ({ historyLoading, language, tickerHistory, t }) => {
  if (historyLoading) {
    return <TickerHistoryLoadingState />;
  }

  if (tickerHistory?.error) {
    return <Typography color="error" sx={{ py: 4, textAlign: 'center' }}>{tickerHistory.error}</Typography>;
  }

  if (!tickerHistory?.history?.length) {
    return <Typography sx={{ py: 4, textAlign: 'center', color: '#94a3b8' }}>{t('behavior.noData')}</Typography>;
  }

  const current = tickerHistory.history[0];

  return (
    <>
      <TickerHistoryCurrentStatusCard current={current} t={t} />
      {tickerHistory.snapshot && <TickerHistorySnapshotCard language={language} snapshot={tickerHistory.snapshot} t={t} />}
      {tickerHistory.stats && (
        <TickerHistoryStatsCard
          currentLabel={current?.behavior_label}
          historyLength={tickerHistory.history.length}
          snapshotLabel={tickerHistory.snapshot?.behavior_label}
          stats={tickerHistory.stats}
          t={t}
        />
      )}
      <TickerHistoryPriceChartCard history={tickerHistory.history} t={t} />
      <TickerHistoryTimelineChart history={tickerHistory.history} stats={tickerHistory.stats} t={t} />
    </>
  );
};

TickerHistoryDrawerBody.propTypes = {
  historyLoading: PropTypes.bool.isRequired,
  language: PropTypes.string.isRequired,
  t: PropTypes.func.isRequired,
  tickerHistory: tickerHistoryShape,
};

TickerHistoryDrawerBody.defaultProps = {
  tickerHistory: null,
};

const TickerHistoryDrawer = ({
  drawerBodyRef,
  drawerExportRef,
  drawerTicker,
  exportingDrawer,
  historyLoading,
  language,
  onClose,
  onExport,
  t,
  tickerHistory,
}) => {
  const snapshot = tickerHistory?.snapshot;
  const currentLabel = snapshot?.behavior_label || tickerHistory?.history?.[0]?.behavior_label;
  const meta = LABEL_META[currentLabel] || LABEL_META.NEUTRAL;
  const sectorLabel = language === 'en'
    ? (snapshot?.sector_en || snapshot?.sector_code)
    : (snapshot?.sector_vi || snapshot?.sector_code);

  return (
    <Drawer
      anchor="right"
      open={!!drawerTicker}
      onClose={onClose}
      slotProps={{ paper: { sx: { width: { xs: '100%', sm: 420 }, p: 0 } } }}
    >
      <Box ref={drawerExportRef} sx={{ display: 'flex', flexDirection: 'column', height: '100%', bgcolor: '#fff' }}>
        <Box
          sx={{
            position: 'relative',
            px: 2.5,
            pt: 1.75,
            pb: 1.5,
            borderBottom: '1px solid #e2e8f0',
            background: `linear-gradient(135deg, ${alpha(meta.color, 0.08)} 0%, #ffffff 70%)`,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: 1.5,
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: meta.gradient,
                color: 'white',
                fontWeight: 800,
                fontSize: '0.82rem',
                letterSpacing: '0.02em',
                boxShadow: `0 4px 12px ${alpha(meta.color, 0.35)}`,
              }}
            >
              {drawerTicker}
            </Box>

            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {tickerHistory?.company_name || '—'}
              </Typography>

              <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.5, mt: 0.6 }}>
                {sectorLabel && (
                  <Chip
                    label={sectorLabel}
                    size="small"
                    sx={{
                      height: 18,
                      fontSize: '0.6rem',
                      bgcolor: 'rgba(255,255,255,0.7)',
                      color: '#475569',
                      border: '1px solid #e2e8f0',
                      '& .MuiChip-label': { px: 0.7 },
                    }}
                  />
                )}
              </Box>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }} data-html2canvas-ignore="true">
              <Tooltip title={t('preparation.detail.exportDrawerImage')} placement="top">
                <span>
                  <IconButton
                    size="small"
                    onClick={onExport}
                    disabled={exportingDrawer || historyLoading}
                    sx={{ color: '#64748b', '&:hover': { color: '#0f172a', bgcolor: '#f1f5f9' } }}
                  >
                    {exportingDrawer
                      ? <CircularProgress size={14} color="inherit" />
                      : <DownloadRounded sx={{ fontSize: 18 }} />}
                  </IconButton>
                </span>
              </Tooltip>
              <IconButton size="small" onClick={onClose} sx={{ color: '#64748b' }}>
                <Close fontSize="small" />
              </IconButton>
            </Box>
          </Box>
        </Box>

        <Box ref={drawerBodyRef} sx={{ p: 2, overflowY: 'auto', flex: 1 }}>
          <TickerHistoryDrawerBody historyLoading={historyLoading} language={language} tickerHistory={tickerHistory} t={t} />
        </Box>
      </Box>
    </Drawer>
  );
};

TickerHistoryDrawer.propTypes = {
  drawerBodyRef: refShape,
  drawerExportRef: refShape,
  drawerTicker: PropTypes.string,
  exportingDrawer: PropTypes.bool.isRequired,
  historyLoading: PropTypes.bool.isRequired,
  language: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  onExport: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
  tickerHistory: tickerHistoryShape,
};

TickerHistoryDrawer.defaultProps = {
  drawerBodyRef: null,
  drawerExportRef: null,
  drawerTicker: null,
  tickerHistory: null,
};

export default TickerHistoryDrawer;